import { TenantInvoiceIssuer } from '../../api/account/accountApiTypes';
import { CustomerClass } from '../../api/organization/organizationApiTypes';

/**
 * What the organization form shows, and under which name — the two rules from
 * the 2026-08-19 staging pass, in one place because the create page and the
 * detail page must not answer them differently.
 */

/* ────────────────────────── Billing details ────────────────────────── */

/**
 * Should the billing section be shown for this tenant?
 *
 * ADR-0012 has two invoice-issuance modes. In `external` the tenant's own ERP
 * owns the invoice and its number; kolula issues nothing, so asking an operator
 * for a VAT id and an invoice address is asking for data the product will never
 * use. In `kolula` the section is the input to a statutory document.
 *
 * Two deliberate asymmetries:
 *
 * - **Unconfigured shows the section.** `invoiceIssuer` is absent until someone
 *   sets it in Account → Payments, and it is undefined for legacy tenants
 *   entirely (they have no operational account record). Hiding on an unset flag
 *   would silently remove billing capture from every tenant that has not been
 *   through that screen — the failure mode CLAUDE.md calls "configuration ships
 *   with its feature, or fails loudly". Only an explicit `external` hides.
 * - **Data already entered keeps the section visible.** Hiding a populated
 *   section does not delete the values, it makes them invisible and
 *   uneditable — the operator cannot even correct a VAT id they can no longer
 *   see. A tenant that switches to `external` therefore keeps the section on
 *   organizations that already have billing data.
 */
export const shouldShowBillingSection = (
    invoiceIssuer: TenantInvoiceIssuer | null | undefined,
    hasBillingData: boolean,
): boolean => invoiceIssuer !== 'external' || hasBillingData;

/* ─────────────────────────── Customer class ─────────────────────────── */

/** Select value meaning "no org-level class → the system default applies". */
export const NO_CLASS_VALUE = '__none__';

/**
 * The customer-class options, and what each is called.
 *
 * The select used to offer `None (system default)` **and** the tenant's
 * system-default class as separate entries. Both resolve to the same tariff —
 * a null org class falls through to the system default — so a tenant with only
 * the seeded default was asked to choose between two spellings of one answer.
 * That was the first thing the staging pass tripped over.
 *
 * So: the system-default class is not offered under its own id. The "no
 * override" option is offered **under that class's name**, because that is the
 * tariff the organization will actually get. One outcome, one entry, and the
 * stored value stays `null` — an organization that inherits keeps inheriting if
 * the tenant later renames its default or points it elsewhere.
 *
 * With only the seeded class this yields a single option ("Standard"), already
 * selected. That is deliberate rather than hiding the field: it shows the axis
 * exists and that an organization can be given its own tariff once the tenant's
 * pricing distinguishes segments.
 *
 * **UNITS DO NOT USE THIS, on purpose.** `OrgUnitsSection` builds its own list
 * because a unit's `null` means "inherit from the organization" — which differs
 * from the system default whenever the org carries a class of its own. There
 * the two entries are two outcomes and both must be offered. Harmonising the
 * two lists would silently change what a unit's blank class means.
 */
export interface CustomerClassOptions {
    /** Select option values, in display order. */
    values: string[];
    /** value → label. */
    labels: Record<string, string>;
}

export const buildCustomerClassOptions = (
    classes: CustomerClass[],
    fallbackLabel: string,
): CustomerClassOptions => {
    const active = classes.filter((c) => c.archivedAt === null);
    const systemDefault = active.find((c) => c.isSystemDefault);
    const explicit = active.filter((c) => !c.isSystemDefault);

    const labels: Record<string, string> = {
        [NO_CLASS_VALUE]: systemDefault?.name ?? fallbackLabel,
    };
    explicit.forEach((c) => {
        labels[c.id] = c.name;
    });

    return { values: [NO_CLASS_VALUE, ...explicit.map((c) => c.id)], labels };
};

/**
 * #1810: name "no override" after the class a booking INHERITS from the
 * organisation (unit) it is attributed to. `buildCustomerClassOptions` names it
 * after the system default because an unattributed admin booking resolves
 * there; an attributed one resolves to its organisation's class, and the
 * select must say the class the price is actually quoted in. Unchanged when
 * nothing is inherited or the class is archived.
 */
export const withInheritedClassLabel = (
    options: CustomerClassOptions,
    classes: CustomerClass[],
    inheritedClassId: string | null | undefined,
): CustomerClassOptions => {
    const inherited = classes.find((c) => c.id === inheritedClassId && c.archivedAt === null);
    return inherited
        ? { ...options, labels: { ...options.labels, [NO_CLASS_VALUE]: inherited.name } }
        : options;
};

/**
 * Does this tenant have anything to choose between?
 *
 * Drives the hint under the select: with one class the honest message is that
 * differentiation is possible but not configured yet, not "pick one".
 */
export const hasCustomerClassChoice = (classes: CustomerClass[]): boolean =>
    classes.filter((c) => c.archivedAt === null && !c.isSystemDefault).length > 0;
